import * as React from 'react'
import { useDispatch } from 'react-redux'
import { setIsStored } from '../../../redux/slices/Alerts'
import {
  Box,
  Paper,
  Grid,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Divider,
} from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import { datasetFormik } from './datasetFormik'
import { Formik, Form } from 'formik'
import Textfield from '../../FormsUI/Textfield'
import Select from '../../FormsUI/Select'
import Modal from '../../FormCreationModal'
import { createProvince, updateProvince } from '../../../services/provinceService'

const STEPS = [
  { label: 'Información Básica', description: 'Datos de la Provincia' },
]

const gridStyle = {
  mt: 0,
  width: '100%',
  ml: 0,
  textAlignLast: 'right',
  justifyContent: 'space-evenly',
  pr: 2,
}

const FormContent = ({ formik, onCancel, onSubmit, initialValues }) => {
  const otherPropsControl = {
    InputProps: { readOnly: false },
    variant: 'outlined',
  }

  const renderField = (field, index) => {
    const child = !field.options ? (
      <Textfield
        {...otherPropsControl}
        {...field}
        type={field.type || 'text'}
      />
    ) : (
      <Select key={`field-${index}`} {...field} {...otherPropsControl} />
    )

    return (
      <Grid
        key={`field-${index}`}
        item
        xs={field.x}
        sx={{ textAlignLast: 'center' }}
      >
        {child}
      </Grid>
    )
  }

  return (
    <Box>
      <Stepper activeStep={0} sx={{ pt: 2, pb: 3 }}>
        {STEPS.map(step => (
          <Step key={step.label}>
            <StepLabel>
              <Typography variant="body2">{step.label}</Typography>
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <Divider />

      <Box sx={{ py: 2, px: 2, backgroundColor: 'grey.50' }}>
        <Typography variant="subtitle2" color="text.secondary">
          Paso 1: {STEPS[0].description}
        </Typography>
      </Box>

      <Divider />

      <Paper
        style={{
          overflow: 'auto',
          borderRadius: 0,
          maxHeight: '50vh',
          minHeight: 300,
        }}
        sx={{ p: 2 }}
      >
        <Grid container spacing={2} sx={{ ...gridStyle }}>
          {formik.STEP1_FIELDS.map((field, index) => renderField(field, index))}
        </Grid>
      </Paper>

      <Divider />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2 }}>
        <Button onClick={onCancel} color="inherit">
          Cancelar
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={onSubmit}
          startIcon={<CheckIcon />}
        >
          {initialValues ? 'Actualizar' : 'Crear'}
        </Button>
      </Box>
    </Box>
  )
}

const ProvinceFormModal = (props, ref) => {
  const dispatch = useDispatch()

  const { initialValues, onClose, onSuccess } = props
  const [formik, setFormik] = React.useState(null)
  const [open, setOpen] = React.useState(false)
  const formikRef = React.useRef(null)

  const handleCancelAction = () => {
    setOpen(false)
    if (onClose) onClose()
  }

  const handleSubmitForm = async () => {
    if (!formikRef.current) return
    formikRef.current.submitForm()
  }

  const handleAddAction = async values => {
    let resultSave = { stored: true }

    const data = {
      nombre: values.nombre,
      idPais: parseInt(values.id_pais, 10),
      habilitado: Boolean(parseInt(values.habilitado, 10)),
    }

    let response
    if (initialValues?.id) {
      response = await updateProvince(initialValues.id, data)
    } else {
      response = await createProvince(data)
    }

    if (!response.success) {
      resultSave = { stored: false, message: response?.message || 'Error al guardar' }
    } else if (onSuccess) {
      onSuccess()
    }

    return resultSave
  }

  React.useImperativeHandle(ref, () => ({ openModal: () => setOpen(true) }))

  React.useEffect(() => {
    if (props.open !== undefined) setOpen(props.open)
  }, [props.open])

  React.useEffect(() => {
    if (!open) return
    datasetFormik().then(variables => {
      if (initialValues) {
        variables.INITIAL_FORMIK_STATE = {
          ...variables.INITIAL_FORMIK_STATE,
          nombre: initialValues.nombre || '',
          id_pais: initialValues.idPais || initialValues.id_pais || '',
          habilitado: initialValues.habilitado === true ? 1 : 0,
        }
      }
      setFormik(variables)
    })
    return () => {
      setFormik(null)
    }
  }, [open, initialValues])

  if (!Boolean(formik)) return null

  return (
    <div>
      <Modal
        open={open}
        title={initialValues ? 'Editar Provincia' : 'Crear una Provincia'}
        description=""
      >
        <Box>
          <Formik
            innerRef={formikRef}
            initialValues={{ ...formik?.INITIAL_FORMIK_STATE }}
            validationSchema={formik?.FORM_VALIDATION}
            onSubmit={async values => {
              const itsStored = await handleAddAction(values)
              handleCancelAction()
              dispatch(setIsStored(itsStored))
            }}
          >
            <Form>
              <FormContent
                formik={formik}
                onCancel={handleCancelAction}
                onSubmit={handleSubmitForm}
                initialValues={initialValues}
              />
            </Form>
          </Formik>
        </Box>
      </Modal>
    </div>
  )
}

export default React.forwardRef(ProvinceFormModal)
